// aiStatsChart.js
import React, { useMemo, useState, useEffect } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ZAxis,
} from "recharts";
import "./TradesDayHourChart.css"; // <-- NEW

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const toMon0 = (jsDay) => (jsDay + 6) % 7;

  export default function TradesDayHourChart({
    docs = [],
    height = 520,
    groupByDate = false,
    onChangeGroupMode, // <- NEW (optional)
  }) {
  const [visibleHits, setVisibleHits] = useState({ tp: true, sl: true, none: false });
  const [scoreTab, setScoreTab] = useState(() => localStorage.getItem("scoreTab") || "BUY");
  useEffect(() => localStorage.setItem("scoreTab", scoreTab), [scoreTab]);

  const scoreColumns = [
    { key: "score", label: "Score" },
    { key: "tp",    label: "TP"   },
    { key: "sl",    label: "SL"   },
    {
      key: "percentGained",
      label: "% Gained",
      render: (v) => <span className="tdh-pl-pos">{fmtPct(v)}</span>,
    },
    {
      key: "percentLost",
      label: "% Lost",
      render: (v) => <span className="tdh-pl-neg">{fmtPct(v)}</span>,
    },
    {
      key: "avgPL",
      label: "Avg P/L",
      render: (v) => <span className={plClass(v)}>{fmtPct(v)}</span>,
    },
  ];

  const {
    bins, weekdayData, dateData, hourData, globalRankMap, hourRepeatCounts,
    scoreDataBuy, scoreDataSell, topDays, topHours, topScores
  } = useMemo(() => {
    const map = new Map();
    const scoreAgg = new Map();

    docs.forEach((doc) => {
      if (!doc?.timestamp) return;
      const dt  = new Date(doc.timestamp);
      const hit = String(doc?.outcome?.targetHit || "none").toLowerCase();

      if (hit === "tp" && !visibleHits.tp) return;
      if (hit === "sl" && !visibleHits.sl) return;
      if (!["tp", "sl"].includes(hit) && !visibleHits.none) return;

      const hour = dt.getHours();
      const dow  = toMon0(dt.getDay());
      const key  = `${dow}-${hour}`;

      const rawToken   = doc?.token || "?";
      const baseSymbol = rawToken.toUpperCase().split("/")[0].split(":")[0].replace(/-PERP$/, "");
      const pl         = typeof doc?.outcome?.profitLoss === "number" ? doc.outcome.profitLoss : 0;

      if (!map.has(key)) {
        map.set(key, {
          x: dow, y: hour,
          tpCount: 0, slCount: 0, noneCount: 0,
          sumPL: 0, sumPLPos: 0, sumPLNeg: 0,
          trades: [],
        });
      }
      const bin = map.get(key);
      if (hit === "tp") bin.tpCount += 1;
      else if (hit === "sl") bin.slCount += 1;
      else bin.noneCount += 1;

      bin.sumPL += pl;
      if (pl > 0) bin.sumPLPos += pl;
      if (pl < 0) bin.sumPLNeg += pl;
      bin.trades.push({
        token: baseSymbol, hit, pl, ts: dt,
        recommendation: doc?.analysis?.recommendation, source: doc?.analysis?.source
      });

      const scoreVal = typeof doc?.analysis?.score === "number" ? doc.analysis.score : null;
      const scoreKey = scoreVal !== null ? scoreVal : "No score";
      const recType  = (doc?.analysis?.recommendation || "").toUpperCase();

      const aggKey = `${scoreKey}__${recType}`;
      if (!scoreAgg.has(aggKey)) {
        scoreAgg.set(aggKey, {
          score: scoreKey, rec: recType,
          total: 0, tp: 0, sl: 0, gained: 0, lost: 0, sumPL: 0
        });
      }
      const s = scoreAgg.get(aggKey);
      s.total += 1;
      if (hit === "tp") s.tp += 1;
      if (hit === "sl") s.sl += 1;
      if (pl > 0) s.gained += 1;
      if (pl < 0) s.lost += 1;
      s.sumPL += pl;
    });

    const bins = [...map.values()].map((b) => {
      const color = b.tpCount > b.slCount ? "#10b981" : b.slCount > b.tpCount ? "#ef4444" : "#64748b";
      let sizeValue = 0;
      if (visibleHits.tp && visibleHits.sl) sizeValue = Math.max(0, b.sumPLPos);
      else if (visibleHits.tp && !visibleHits.sl) sizeValue = Math.max(0, b.sumPLPos);
      else if (!visibleHits.tp && visibleHits.sl) sizeValue = Math.abs(Math.min(0, b.sumPLNeg));
      return { ...b, total: b.tpCount + b.slCount + b.noneCount, color, sizeValue };
    });




// ---- Build summaries directly from docs to avoid double counting ----
const weekdayAgg = Array.from({ length: 7 }, () => ({ tp: 0, sl: 0, pl: 0 }));
const weekdayHourPL = Array.from({ length: 7 }, () => ({})); // dow -> { hour: sumPL }
const hourSummary = {}; // keep your hour table

const dateAgg = new Map();

// apply same hit-visibility filter that bins used
function includeDoc(doc) {
  const hit = String(doc?.outcome?.targetHit || "none").toLowerCase();
  if (hit === "tp" && !visibleHits.tp) return false;
  if (hit === "sl" && !visibleHits.sl) return false;
  if (!["tp", "sl"].includes(hit) && !visibleHits.none) return false;
  return true;
}

docs.forEach((doc) => {
  if (!doc?.timestamp || !includeDoc(doc)) return;

  const dt   = new Date(doc.timestamp);
  const dow  = toMon0(dt.getDay());
  const hour = dt.getHours();
  const hit  = String(doc?.outcome?.targetHit || "none").toLowerCase();
  const pl   = typeof doc?.outcome?.profitLoss === "number" ? doc.outcome.profitLoss : 0;

  // weekday totals
  if (hit === "tp") weekdayAgg[dow].tp += 1;
  else if (hit === "sl") weekdayAgg[dow].sl += 1;
  weekdayAgg[dow].pl += pl;

  // weekday x hour PL (for "best hours")
  weekdayHourPL[dow][hour] = (weekdayHourPL[dow][hour] || 0) + pl;

  // hour totals (for "Summary by Hour")
  if (!hourSummary[hour]) hourSummary[hour] = { tp: 0, sl: 0, pl: 0 };
  if (hit === "tp") hourSummary[hour].tp += 1;
  else if (hit === "sl") hourSummary[hour].sl += 1;
  hourSummary[hour].pl += pl;

// date totals (for "Summary by Date" + per-hour PL for "best hours")
const dateStr = dt.toISOString().slice(0, 10);
if (!dateAgg.has(dateStr)) {
  dateAgg.set(dateStr, { 
    tp: 0, sl: 0, pl: 0, 
    hourPL: {}, 
    plBuy: 0,          // <-- NEW: long-only P/L
    plSell: 0          // <-- NEW: short-only P/L
  });
}
const d = dateAgg.get(dateStr);
if (hit === "tp") d.tp += 1;
else if (hit === "sl") d.sl += 1;
d.pl += pl;
d.hourPL[hour] = (d.hourPL[hour] || 0) + pl;

// NEW: attribute P/L by side using recommendation
const recRaw = (doc?.analysis?.recommendation || "").toUpperCase();
const isBuy  = ["BUY", "STRONG BUY", "LONG", "STRONG LONG"].includes(recRaw);
const isSell = ["SELL", "STRONG SELL", "SHORT", "STRONG SHORT"].includes(recRaw);
if (isBuy)  d.plBuy  += pl;
if (isSell) d.plSell += pl;
});

// helper to get top 3 hours per weekday by PL
const best3ForDow = (dow) =>
  Object.entries(weekdayHourPL[dow])
    .map(([h, pl]) => ({ hour: Number(h), pl }))
    .sort((a, b) => b.pl - a.pl)
    .slice(0, 3);

// Build weekdayData (always 7 rows, Mon..Sun) with best hours
const weekdayData = DOW_LABELS.map((label, i) => ({
  day: label,
  tp:  weekdayAgg[i].tp,
  sl:  weekdayAgg[i].sl,
  pl:  weekdayAgg[i].pl,
  best: best3ForDow(i),
}));

// Build hourData (00..23)
const hourData = Object.entries(hourSummary)
  .map(([h, data]) => ({ hour: `${String(h).padStart(2, "0")}:00`, ...data }))
  .sort((a, b) => b.pl - a.pl);
  

const best3FromHourPL = (hourPL) =>
  Object.entries(hourPL)
    .map(([h, v]) => ({ hour: Number(h), pl: v }))
    .sort((a, b) => b.pl - a.pl)
    .slice(0, 3);

const dateData = Array.from(dateAgg.entries())
  .map(([date, d]) => ({
    date,
    tp: d.tp,
    sl: d.sl,
    pl: d.pl,
    plBuy: d.plBuy,     // <-- NEW
    plSell: d.plSell,   // <-- NEW
    best: best3FromHourPL(d.hourPL),
    leader: (d.plBuy === d.plSell) ? "Tie" : (d.plBuy > d.plSell ? "LONG" : "SHORT") // optional
  }))
  .sort((a, b) => new Date(b.date) - new Date(a.date));





    const hourRepeatCounts = {};
    weekdayData.forEach(w => (w.best || []).forEach(b => {
      hourRepeatCounts[b.hour] = (hourRepeatCounts[b.hour] || 0) + 1;
    }));
    const globalRankMap = {};
    Object.entries(hourRepeatCounts)
      .map(([h, count]) => ({ hour: Number(h), count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .forEach((o, i) => (globalRankMap[o.hour] = i + 1));



    // roll up score data (BUY vs SELL)
    const scoreRows = [];
    docs.forEach((doc) => {
      if (!doc?.timestamp) return;
      const hit = String(doc?.outcome?.targetHit || "none").toLowerCase();
      if (hit === "tp" && !visibleHits.tp) return;
      if (hit === "sl" && !visibleHits.sl) return;
      if (!["tp", "sl"].includes(hit) && !visibleHits.none) return;

      const recRaw   = (doc?.analysis?.recommendation || "").toUpperCase();
      const isBuy    = ["BUY", "STRONG BUY", "LONG", "STRONG LONG"].includes(recRaw);
      const isSell   = ["SELL", "STRONG SELL", "SHORT", "STRONG SHORT"].includes(recRaw);
      if (!isBuy && !isSell) return;

      const pl = typeof doc?.outcome?.profitLoss === "number" ? doc.outcome.profitLoss : 0;
      const s  = typeof doc?.analysis?.score === "number" ? doc.analysis.score : null;
      if (s === null || Number.isNaN(s)) return;

      const score = Math.round(Number(s) * 10) / 10;

      scoreRows.push({
        side: isBuy ? "BUY" : "SELL",
        score,
        hit,
        pl,
      });
    });

    function rollupByScore(rows) {
      const agg = new Map();
      rows.forEach((r) => {
        if (!agg.has(r.score)) {
          agg.set(r.score, {
            score: r.score,
            total: 0, tp: 0, sl: 0, gained: 0, lost: 0, sumPL: 0,
          });
        }
        const a = agg.get(r.score);
        a.total += 1;
        if (r.hit === "tp") a.tp += 1;
        if (r.hit === "sl") a.sl += 1;
        if (r.pl > 0) a.gained += 1;
        if (r.pl < 0) a.lost += 1;
        a.sumPL += r.pl;
      });

      return [...agg.values()]
        .map((a) => ({
          score: a.score,
          tp: a.tp,
          sl: a.sl,
          percentGained: a.total ? (a.gained / a.total) * 100 : 0,
          percentLost:   a.total ? (a.lost   / a.total) * 100 : 0,
          avgPL:         a.total ? a.sumPL / a.total : 0,
        }))
        .sort((x, y) => Number(y.score) - Number(x.score));
    }

    const scoreDataBuy  = rollupByScore(scoreRows.filter(r => r.side === "BUY"));
    const scoreDataSell = rollupByScore(scoreRows.filter(r => r.side === "SELL"));

    const topDays = [...weekdayData].slice(0, 3);

    const combinedScores = [
      ...scoreDataBuy.map((r)  => ({ ...r, side: "BUY"  })),
      ...scoreDataSell.map((r) => ({ ...r, side: "SELL" })),
    ];
    const topScores = combinedScores
      .sort((a, b) => b.avgPL - a.avgPL)
      .slice(0, 6);

    const TOP_HOURS_N = 6;
    const topHours = [...hourData].sort((a, b) => b.pl - a.pl).slice(0, TOP_HOURS_N);

    return { bins, weekdayData, dateData, hourData, globalRankMap, hourRepeatCounts, scoreDataBuy, scoreDataSell, topDays, topScores, topHours };
  }, [docs, visibleHits, groupByDate]);

  /*** UI helpers ***/
  const Chip = ({ className = "", children }) => (
    <span className={`tdh-chip ${className}`}>{children}</span>
  );

  const Card = ({ title, children }) => (
    <div className="tdh-card">
      <div className="tdh-card__header">
        <h4 className="tdh-card__title">{title}</h4>
      </div>
      <div className="tdh-card__body">{children}</div>
    </div>
  );

  const Row = ({ left, right }) => (
    <div className="tdh-row">
      <div className="tdh-row__left">{left}</div>
      <div className="tdh-row__right">{right}</div>
    </div>
  );

  const sideBadgeCls = (side) => (side === "BUY" ? "tdh-chip--green" : "tdh-chip--red");
  const fmtPct = (n) => `${(n ?? 0).toFixed(2)}%`;
  const plClass = (n) => (n >= 0 ? "tdh-pl-pos" : "tdh-pl-neg");

  const rankClass = (rank) =>
    rank === 1 ? "tdh-rank--1"
      : rank === 2 ? "tdh-rank--2"
      : rank === 3 ? "tdh-rank--3"
      : "tdh-rank--0";

  const bestHourBadge = (row, idx) => {
    const entry = row.best?.[idx];
    if (!entry) return "–";
    const rank  = globalRankMap?.[entry.hour] ?? null;
    const count = hourRepeatCounts?.[entry.hour] ?? 0;
    const label = `${String(entry.hour).padStart(2, "0")}:00 (${fmtPct(entry.pl)})`;
    return (
      <span className={`tdh-rank ${rankClass(rank)}`} title={`repeats: ${count} • global rank: ${rank ?? "—"}`}>
        {label}
      </span>
    );
  };

  function SummaryTable({ title, rows, columns, rowKey }) {
    const [sort, setSort] = useState({ key: columns[0].key, dir: "desc" });
    const sorted = [...rows].sort((a, b) => {
      const av = a[sort.key]; const bv = b[sort.key];
      if (typeof av === "number" && typeof bv === "number") {
        return sort.dir === "asc" ? av - bv : bv - av;
      }
      return sort.dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    const toggle = (key) =>
      setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

    return (
      <div className="tdh-card tdh-table-card">
        <div className="tdh-card__header">
          <h4 className="tdh-card__title">{title}</h4>
        </div>
        <div className="tdh-table-wrap">
          <table className="tdh-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => toggle(c.key)}
                    className={`tdh-th ${sort.key === c.key ? (sort.dir === "asc" ? "is-asc" : "is-desc") : ""}`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r[rowKey]}>
                  {columns.map((c) => (
                    <td key={c.key} className="tdh-td">
                      {c.render ? c.render(r[c.key], r) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /*** Recharts tooltip & dot ***/
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    return (
      <div className="tdh-tooltip">
        <div className="tdh-tooltip__title">
          {DOW_LABELS[p.x]} @ {String(p.y).padStart(2, "0")}:00
        </div>
        <div className="tdh-tooltip__meta">Trades: {p.total}</div>
        <div className="tdh-tooltip__kv">TP: <span className="tdh-pl-pos">{p.tpCount}</span></div>
        <div className="tdh-tooltip__kv">SL: <span className="tdh-pl-neg">{p.slCount}</span></div>
        <div className="tdh-tooltip__kv">None: <span className="tdh-muted">{p.noneCount}</span></div>
        <div className="tdh-tooltip__kv tdh-tooltip__sum">
          Σ P/L: <span className={plClass(p.sumPL)}>{p.sumPL.toFixed(2)}%</span>
        </div>
      </div>
    );
  };

  const CustomDot = ({ cx, cy, payload }) => {
    const r = 14 + payload.sizeValue * 1.5;
    const fit = r * 1.5;
    const sumText = (payload.sumPL >= 0 ? "+" : "") + payload.sumPL.toFixed(1) + "%";
    return (
      <g>
        <circle cx={cx} cy={cy} r={r} fill={payload.color} opacity={0.95} stroke="#0f172a" strokeWidth={1} />
        <text x={cx} y={cy + 2} textAnchor="middle" fontSize={10} fontWeight={700} fill="#fff"
              textLength={fit} lengthAdjust="spacingAndGlyphs">
          {sumText}
        </text>
      </g>
    );
  };

  const xTicks = [0, 1, 2, 3, 4, 5, 6];
  const xDomain = [-1, 7];
  const yDomain = [-1, 24];
  const yTicks  = Array.from({ length: 24 }, (_, i) => i);

  if (!bins.length) {
    return <div className="tdh-empty">No trades to visualize yet.</div>;
  }

  return (
    <div className="tdh">
      {/* Highlights */}
      <div className="tdh-cards">
        <Card title="Top Weekdays (by Total P/L)">
          {topDays.length === 0 ? (
            <div className="tdh-muted">No data.</div>
          ) : (
            <div className="tdh-divider">
              {topDays.map((d) => (
                <Row
                  key={d.day}
                  left={
                    <>
                      <Chip className="tdh-chip--slate">{d.day}</Chip>
                      <Chip className="tdh-chip--emerald-outline">TP {d.tp}</Chip>
                      <Chip className="tdh-chip--rose-outline">SL {d.sl}</Chip>
                    </>
                  }
                  right={<Chip className={`tdh-chip--light ${plClass(d.pl)}`}>{fmtPct(d.pl)}</Chip>}
                />
              ))}
            </div>
          )}
        </Card>

        <Card title="Top Scores (by Avg P/L)">
          {topScores.length === 0 ? (
            <div className="tdh-muted">No data.</div>
          ) : (
            <div className="tdh-divider">
              {topScores.map((s, i) => (
                <Row
                  key={`${s.side}-${s.score}-${i}`}
                  left={
                    <>
                      <Chip className={sideBadgeCls(s.side)}>{s.side}</Chip>
                      <span className="tdh-strong">Score {s.score}</span>
                    </>
                  }
                  right={
                    <>
                      <Chip className="tdh-chip--emerald-outline">%G {fmtPct(s.percentGained)}</Chip>
                      <Chip className="tdh-chip--rose-outline">%L {fmtPct(s.percentLost)}</Chip>
                      <Chip className={`tdh-chip--light ${plClass(s.avgPL)}`}>{fmtPct(s.avgPL)}</Chip>
                    </>
                  }
                />
              ))}
            </div>
          )}
        </Card>

        <Card title="Top Hours (by Total P/L)">
          {topHours.length === 0 ? (
            <div className="tdh-muted">No data.</div>
          ) : (
            <div className="tdh-divider">
              {topHours.map((h) => (
                <Row
                  key={h.hour}
                  left={
                    <>
                      <Chip className="tdh-chip--slate">{h.hour}</Chip>
                      <Chip className="tdh-chip--emerald-outline">TP {h.tp}</Chip>
                      <Chip className="tdh-chip--rose-outline">SL {h.sl}</Chip>
                    </>
                  }
                  right={<Chip className={`tdh-chip--light ${plClass(h.pl)}`}>{fmtPct(h.pl)}</Chip>}
                />
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Header + toggles */}
      <div className="tdh-header">
        <h3 className="tdh-title">Trade Map</h3>
      </div>

      <div className="tdh-pills">
        <button
          onClick={() => setVisibleHits((p) => ({ ...p, tp: !p.tp }))}
          className={`tdh-pill tdh-pill--green ${visibleHits.tp ? "is-active" : ""}`}
        >
          TP
        </button>
        <button
          onClick={() => setVisibleHits((p) => ({ ...p, sl: !p.sl }))}
          className={`tdh-pill tdh-pill--red ${visibleHits.sl ? "is-active" : ""}`}
        >
          SL
        </button>
      </div>

      {/* Chart */}
      <div className="tdh-chartbox">
        <div className="tdh-chartbox-hide">
        <ResponsiveContainer width="100%" height={height}>
          <ScatterChart margin={{ top: 40, right: 40, bottom: 40, left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="x"
              domain={xDomain}
              ticks={xTicks}
              interval={0}
              allowDecimals={false}
              tickMargin={12}
              tickFormatter={(v) => DOW_LABELS[v] ?? ""}
              tick={{ fontSize: 12 }}
              label={{ position: "insideBottom", offset: -10 }}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={yDomain}
              ticks={yTicks}
              tickCount={24}
              allowDecimals={false}
              tick={{ fontSize: 12 }}
              label={{ angle: -90, position: "insideLeft" }}
            />
            <ZAxis type="number" dataKey={() => 1} range={[1, 1]} />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: "3 3" }} />
            <Legend
              payload={[
                { value: "TP>SL", type: "circle", id: "moreTP", color: "#10b981" },
                { value: "SL>TP", type: "circle", id: "moreSL", color: "#ef4444" },
                { value: "Tie",   type: "circle", id: "tie",    color: "#64748b" },
              ]}
            />
            <Scatter data={bins} shape={<CustomDot />} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>


    {/* Weekday/Date toggle (like BUY/SELL tabs) */}
    <div className="tdh-scoretabs">
      <div className="tdh-scoretabs__bar">
        <h4 className="tdh-card__title tdh-scoretabs__title">
          {groupByDate ? "Summary by Date" : "Summary by Weekday"}
        </h4>
        <div className="tdh-scoretabs__pills">
          <button
            onClick={() => onChangeGroupMode?.(false)}
            className={`tdh-tab ${!groupByDate ? "is-active" : ""}`}
            title="Group by weekday"
          >
            Weekday
          </button>
          <button
            onClick={() => onChangeGroupMode?.(true)}
            className={`tdh-tab ${groupByDate ? "is-active" : ""}`}
            title="Group by date"
          >
            Date
          </button>
        </div>
      </div>
    </div>

        <SummaryTable
        title={groupByDate ? "Summary by Date" : "Summary by Weekday"}
        rowKey={groupByDate ? "date" : "day"}
        rows={groupByDate ? dateData : weekdayData}
        columns={
            groupByDate
            ? [
                { key: "date", label: "Date" },
                { key: "tp", label: "TP" },
                { key: "sl", label: "SL" },
                { key: "pl", label: "Total P/L", render: (v) => <span className={plClass(v)}>{fmtPct(v)}</span> },

                // NEW: split by side
                { key: "plBuy",  label: "Long P/L",  render: (v) => <span className={plClass(v)}>{fmtPct(v)}</span> },
                { key: "plSell", label: "Short P/L", render: (v) => <span className={plClass(v)}>{fmtPct(v)}</span> },

                // Keep your best-hour badges
                { key: "best1", label: "Best Hour 1", render: (_, row) => bestHourBadge(row, 0) },
                { key: "best2", label: "Best Hour 2", render: (_, row) => bestHourBadge(row, 1) },
                { key: "best3", label: "Best Hour 3", render: (_, row) => bestHourBadge(row, 2) },

                // (Optional) quick winner tag:
                // { key: "leader", label: "Leader", render: (v) => 
                //    <span className={`tdh-chip ${v === "LONG" ? "tdh-chip--green" : v === "SHORT" ? "tdh-chip--red" : "tdh-chip--slate"}`}>{v}</span> 
                // },
                ]
            : [
                { key: "day", label: "Day" },
                { key: "tp", label: "TP" },
                { key: "sl", label: "SL" },
                { key: "pl", label: "Total P/L", render: (v) => <span className={plClass(v)}>{fmtPct(v)}</span> },
                { key: "best1", label: "Best Hour 1", render: (_, row) => bestHourBadge(row, 0) },
                { key: "best2", label: "Best Hour 2", render: (_, row) => bestHourBadge(row, 1) },
                { key: "best3", label: "Best Hour 3", render: (_, row) => bestHourBadge(row, 2) },
                ]
        }
        />


        {/* Scores tabbed */}
        <div className="tdh-scoretabs">
          <div className="tdh-scoretabs__bar">
            <h4 className="tdh-card__title tdh-scoretabs__title">Summary by Score</h4>
            <div className="tdh-scoretabs__pills">
              <button
                onClick={() => setScoreTab("BUY")}
                className={`tdh-tab tdh-tab--buy ${scoreTab === "BUY" ? "is-active" : ""}`}
                title="BUY / LONG"
              >
                BUY
              </button>
              <button
                onClick={() => setScoreTab("SELL")}
                className={`tdh-tab tdh-tab--sell ${scoreTab === "SELL" ? "is-active" : ""}`}
                title="SELL / SHORT"
              >
                SELL
              </button>
            </div>
          </div>

          {scoreTab === "BUY" ? (
            <SummaryTable title="Scores — BUY / LONG"  rowKey="score" rows={scoreDataBuy}  columns={scoreColumns} />
          ) : (
            <SummaryTable title="Scores — SELL / SHORT" rowKey="score" rows={scoreDataSell} columns={scoreColumns} />
          )}
        </div>

        {/* Hour table */}
        <SummaryTable
          title="Summary by Hour"
          rowKey="hour"
          rows={hourData}
          columns={[
            { key: "hour", label: "Hour" },
            { key: "tp",   label: "TP"  },
            { key: "sl",   label: "SL"  },
            { key: "pl",   label: "Total P/L", render: (v) => <span className={plClass(v)}>{fmtPct(v)}</span> },
          ]}
        />
      </div>
    </div>
  );
}
